import { truncateUtf16CodeUnits } from '../fs/truncateUtf16CodeUnits';
import { hasFlag, isFunc, isNum, isStr } from './common';

export function padLeft0(input: string | number, pad = 8): string {
	return input.toString().padStart(pad, '0');
}

export function prettyHex(input: number, pad = 8): string {
	if (Number.isInteger(input)) {
		const str = input.toString(16);
		return str.toUpperCase().padStart(pad, '0');
	} else {
		const str = (input >>> 0).toString(16);
		return str.toUpperCase().padStart(pad, '0');
	}
}

export function prettyHex2(input: number, padBytes = 4): string {
	if (Number.isInteger(input)) {
		const str = input.toString(16);
		return '0x' + str.toUpperCase().padStart(padBytes * 2, '0');
	} else {
		const str = (input >>> 0).toString(16);
		return '0x' + str.toUpperCase().padStart(padBytes * 2, '0');
	}
}

export function prettyHexLower(input: number, pad = 8): string {
	if (Number.isInteger(input)) {
		const str = input.toString(16);
		return padLeft0(str, pad);
	} else {
		const str = (input >>> 0).toString(16);
		return padLeft0(str, pad);
	}
}

export function prettyBin(input: number, pad = 8): string {
	if (Number.isInteger(input)) {
		const str = input.toString(2);
		return str.toUpperCase().padStart(pad, '0');
	} else {
		const str = (input >>> 0).toString(2);
		return str.toUpperCase().padStart(pad, '0');
	}
}

export function prettyBin2(input: number, padBytes = 4): string {
	if (Number.isInteger(input)) {
		const str = input.toString(2);
		return '0b' + str.toUpperCase().padStart(padBytes * 8, '0');
	} else {
		const str = (input >>> 0).toString(2);
		return '0b' + str.toUpperCase().padStart(padBytes * 8, '0');
	}
}

export function escapeNonAscii(str: string): string {
	let output = '';
	let cp: number;

	for (const ch of str) {
		if (ch === '\\') {
			output += '\\\\';
		} else if (ch >= ' ' && ch <= '~') {
			// Use as is
			output += ch;
		} else if (ch === '\0') {
			output += '\\0';
			// } else if (ch === '\t') {
			// output += '\\t';
		} else if (ch === '\n') {
			output += '\\n';
		} else if (ch === '\r') {
			output += '\\r';
		} else {
			cp = ch.codePointAt(0)!;
			if (cp < 256) {
				output += '\\x' + cp.toString(16).toUpperCase().padStart(2, '0');
			} else {
				output += '\\u' + cp.toString(16).toUpperCase().padStart(4, '0');
			}
		}
	}

	return output;
}

export function escapeNonPrintable(str: string): string {
	let output = '';

	for (const _ch of str) {
		let ch: string;

		const cp = _ch.codePointAt(0)!;
		if (cp === 92) {
			ch = '\\\\';
		} else if (
			cp < 9 ||
			cp === 11 ||
			cp === 12 ||
			(cp > 13 && cp < 32) ||
			(cp >= 0x7f && cp <= 0x9f)
		) {
			// Escape low controls
			ch = '\\u' + cp.toString(16).toUpperCase().padStart(4, '0');
		} else {
			// Use as is
			ch = _ch;
		}

		output += ch;
	}

	return output;
}

export function escapeNonPrintableAndWs(str: string): string {
	let output = '';

	for (const _ch of str) {
		let ch: string;

		const cp = _ch.codePointAt(0)!;

		if (cp === 0) {
			ch = '\\0';
		} else if (cp === 9) {
			ch = '\\t';
		} else if (cp === 10) {
			ch = '\\n';
		} else if (cp === 13) {
			ch = '\\r';
		} else if (
			cp < 9 ||
			cp === 11 ||
			cp === 12 ||
			(cp > 13 && cp < 32) ||
			(cp >= 0x7f && cp <= 0x9f) ||
			// no-break space
			cp === 0x00a0 ||
			// zero width space
			cp === 0x200b ||
			// zero width no-break space
			cp === 0xfeff
		) {
			// Escape low controls
			ch = '\\u' + cp.toString(16).padStart(4, '0');
		} else {
			// Use as is
			ch = _ch;
		}

		output += ch;
	}

	return output;
}

const DECIMALS = 2;
export function humanFileSize(bytes: number, si = true): string {
	const thresh = si ? 1000 : 1024;
	if (Math.abs(bytes) < thresh) {
		return bytes + ' B';
	}
	const units = si
		? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
		: ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
	let u = -1;
	do {
		bytes /= thresh;
		++u;
	} while (Math.abs(bytes) >= thresh && u < units.length - 1);
	return bytes.toFixed(DECIMALS) + ' ' + units[u];
}

export function formatBytes(num: number): string {
	return num < 1000 ? num + ' B' : `${humanFileSize(num, true)}, ${humanFileSize(num, false)}`;
}

export function getFlagStr<T>(value: number, map: T): string {
	const matches: string[] = [];
	for (const key in map) {
		const num = map[key];
		if (isNum(num) && hasFlag(value, num)) {
			matches.push(key);
		}
	}

	return matches.join(', ');
}

export function getEnumStr<T>(value: number, map: T): string {
	for (const key in map) {
		const num = map[key];
		if (isNum(num) && num === value) {
			return key;
		}
	}

	return '<unknown>';
}

export function getEnumStrAll<T>(value: number, map: T): string {
	const list: string[] = [];

	for (const key in map) {
		const num = map[key];
		if (isNum(num) && num === value) {
			list.push(key);
		}
	}

	return list.length ? list.join(' / ') : '<unknown>';
}

export function humanDuration(ms: number): string {
	if (ms < 1000) {
		return `${ms} ms`;
	} else if (ms < 1000 * 60) {
		return `${(ms / 1000).toFixed(DECIMALS)} s`;
	} else if (ms < 1000 * 60 * 60) {
		return `${(ms / 1000 / 60).toFixed(DECIMALS)} m`;
	} else if (ms < 1000 * 60 * 60 * 24) {
		return `${(ms / 1000 / 60 / 60).toFixed(DECIMALS)} h`;
	} else {
		return `${(ms / 1000 / 60 / 60 / 24).toFixed(DECIMALS)} d`;
	}
}

/**
 * @param err
 * @returns
 */
export function errToShortString(err: unknown): string {
	switch (typeof err) {
		case 'bigint':
			return `<error object is bigint [${err}]>`;
		case 'boolean':
			return `<error object is literal [${err}]>`;
		case 'function':
			return `<error object is a literal function>`;
		case 'number':
			return `<error object is number [${err.toFixed(3)}]>`;
		case 'object':
			if (err === null) {
				return `<error object is literal [null]>`;
			} else if ('message' in err && isStr(err.message) && err.message.length > 0) {
				return err.message;
			} else if ('stack' in err && isStr(err.stack) && err.stack.length > 0) {
				return err.stack.split(/\r?\n/)[0];
			} else if ('toString' in err && isFunc(err.toString)) {
				return err.toString().split(/\r?\n/)[0];
			} else {
				return `<error object is unknown object type>`;
			}
		case 'string':
			return `<error object is string "${err}">`;
		case 'symbol':
			return `<error object is literal symbol [${err.toString()}]>`;
		case 'undefined':
			return `<error object is literal [${err}]>`;
		default:
			return `<error object is unknown, type: ${typeof err}>`;
	}
}

/**
 * @param err
 * @returns
 */
export function errToLongString(err: unknown): string {
	if (typeof err === 'object' && err !== null && 'stack' in err && isStr(err.stack)) {
		return err.stack;
	} else {
		return errToShortString(err);
	}
}

export function formatPercent(num: number, total: number): string {
	return total === 0 ? '-' : ((100 * num) / total).toFixed(2) + '%';
}

export function formatOutOf(num: number, total: number): string {
	return total === 0
		? `${num.toLocaleString()}/${total.toLocaleString()}`
		: `${num.toLocaleString()}/${total.toLocaleString()} (${formatPercent(num, total)})`;
}

export function formatOutOf2(num: number, total: number): string {
	return `${num} (${formatPercent(num, total)})`;
}

/**
 * Used to provide a plural s
 * @param num
 */
export function s(num: number): string {
	return num === 1 ? '' : 's';
}

export function ies(num: number): string {
	return num === 1 ? 'y' : 'ies';
}

export function es(num: number): string {
	return num === 1 ? '' : 'es';
}

export function were(num: number): string {
	return num === 1 ? 'was' : 'were';
}

export function have(num: number): string {
	return num === 1 ? 'has' : 'have';
}

export function are(num: number): string {
	return num === 1 ? 'is' : 'are';
}

export function ellipsize(str: string, length: number): string {
	return truncateUtf16CodeUnits(str, length, {
		ellipsis: '…',
	});
}

let currencyFormatter: Map<string, Intl.NumberFormat> | null = null;
function getFormatter(currency: string): Intl.NumberFormat {
	if (!currencyFormatter) {
		currencyFormatter = new Map();
	}

	if (!currencyFormatter.has(currency)) {
		currencyFormatter.set(
			currency,
			new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: currency,
				currencyDisplay: 'symbol',
			})
		);
	}

	return currencyFormatter.get(currency)!;
}

/**
 * Formats a currency value (provided in normalized units, day to day currency usage)
 * @param num Amount in normalized units, e.g. as fractional for currency with non-zero minor units
 * @param currency Currency code, e.g. 'USD', 'CAD', 'JPY'
 * @returns The formatted currency as it should be displayed to the user
 */
export function formatCurrencyNormalized(num: number, currency: string): string {
	return getFormatter(currency).format(num);
}

/**
 * Formats a currency value (provided in minor units, e.g. as cents for currency with non-zero minor units like USD)
 * @param num Amount in minor units, e.g. as cents for currency with non-zero minor units like USD
 * @param currency Currency code, e.g. 'USD', 'CAD', 'JPY'
 * @param currencyMinorUnits Number of minor units for the currency, e.g. 2 for USD, 0 for JPY
 * @returns The formatted currency as it should be displayed to the user
 */
export function formatCurrencyMinorUnits(
	num: number,
	currency: string,
	currencyMinorUnits: number
): string {
	const formatter = getFormatter(currency);
	const mult = Math.pow(10, currencyMinorUnits);
	return formatter.format(num / mult);
}
